import React, { useEffect, useMemo, useState } from "react";
import { researchApi } from "../../../api/researchApi";
import DeltaTopology from "./DeltaTopology";
import DeltaInspector from "./DeltaInspector";
import ResultantComparison from "./ResultantComparison";
import ComparativeBoundary from "./ComparativeBoundary";

function first(object, keys, fallback = null) {
  if (!object || typeof object !== "object") return fallback;
  for (const key of keys) {
    if (object[key] !== undefined && object[key] !== null) {
      return object[key];
    }
  }
  return fallback;
}

function number(object, keys, fallback = null) {
  const value = first(object, keys, null);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function recursiveValues(value, wantedKeys, output = []) {
  if (!value || typeof value !== "object") return output;

  if (Array.isArray(value)) {
    for (const item of value) recursiveValues(item, wantedKeys, output);
    return output;
  }

  for (const [key, child] of Object.entries(value)) {
    if (wantedKeys.includes(key)) {
      const parsed = Number(child);
      if (Number.isFinite(parsed)) output.push(parsed);
    }
    if (child && typeof child === "object") {
      recursiveValues(child, wantedKeys, output);
    }
  }

  return output;
}

function recursiveNumber(object, keys, fallback = null) {
  const direct = number(object, keys, null);
  if (direct !== null) return direct;
  const values = recursiveValues(object, keys, []);
  return values.length ? values[0] : fallback;
}

function preferDerived(receiptValue, derivedValue) {
  const receipt = Number(receiptValue);
  const derived = Number(derivedValue);

  if (!Number.isFinite(receipt)) {
    return Number.isFinite(derived) ? derived : null;
  }

  if (receipt === 0 && Number.isFinite(derived) && derived !== 0) {
    return derived;
  }

  return receipt;
}

function array(object, keys) {
  const value = first(object, keys, []);
  return Array.isArray(value) ? value : [];
}

function deltaId(value) {
  if (typeof value === "string") return value;
  return first(value, ["id", "delta_id", "comparative_delta_id"], null);
}

function stateId(value) {
  if (typeof value === "string") return value;
  return first(value, ["id", "state_id", "configuration_state_id"], null);
}

function stateSummary(raw = {}, knownStates = []) {
  const id = stateId(raw);
  const known = knownStates.find((item) => item.id === id) || {};

  return {
    id,
    configurationId: first(
      raw,
      ["configuration_id"],
      first(known, ["configuration_id"], null)
    ),
    configurationLabel: first(
      raw,
      ["configuration_label", "label"],
      first(known, ["configuration_label"], null)
    ),
    orientation: first(
      raw,
      ["orientation", "resultant_orientation", "status"],
      first(known, ["orientation", "resultant_orientation", "status"], "unresolved")
    ),
    rootCount:
      number(raw, ["independent_root_count", "root_count"]) ??
      number(known, ["independent_root_count", "root_count"]),
    pathwayCount:
      number(raw, ["pathway_count", "configuration_pathway_count"]) ??
      number(known, ["pathway_count", "configuration_pathway_count"]),
    convergence:
      number(raw, ["convergence", "convergence_score"]) ??
      number(known, ["convergence", "convergence_score"]),
    divergence:
      number(raw, ["divergence", "divergence_score"]) ??
      number(known, ["divergence", "divergence_score"]),
  };
}

function findStatePair(payload = {}, knownStates = [], currentStateId = null) {
  const arrayPair = first(payload, ["states", "comparison_states", "state_pair"], null);
  if (Array.isArray(arrayPair) && arrayPair.length >= 2) {
    return [
      stateSummary(arrayPair[0], knownStates),
      stateSummary(arrayPair[1], knownStates),
    ];
  }

  const leftRaw = first(
    payload,
    ["left_state", "state_a", "from_state", "source_state", "k0"],
    null
  );
  const rightRaw = first(
    payload,
    ["right_state", "state_b", "to_state", "target_state", "k1"],
    null
  );

  if (leftRaw || rightRaw) {
    return [
      stateSummary(leftRaw || {}, knownStates),
      stateSummary(rightRaw || {}, knownStates),
    ];
  }

  const leftId = first(
    payload,
    ["left_state_id", "state_a_id", "from_state_id", "source_state_id", "k0_state_id"],
    null
  );
  const rightId = first(
    payload,
    ["right_state_id", "state_b_id", "to_state_id", "target_state_id", "k1_state_id"],
    null
  );

  if (leftId || rightId) {
    return [
      stateSummary({ id: leftId }, knownStates),
      stateSummary({ id: rightId }, knownStates),
    ];
  }

  const current = knownStates.find((state) => state.id === currentStateId);
  const other = knownStates.find((state) => state.id !== currentStateId);

  return [
    stateSummary(current || knownStates[0] || {}, knownStates),
    stateSummary(other || knownStates[1] || {}, knownStates),
  ];
}

function normalizeRootStanceChanges(payload = {}) {
  const raw = array(payload, [
    "root_stance_changes",
    "stance_changes",
    "root_changes",
  ]);

  return raw.map((item, index) => ({
    id: first(item, ["id", "change_id"], `stance-${index}`),
    rootId: first(item, ["root_id", "study_root_id"], null),
    label: first(item, ["label", "change_label"], null),
    status: first(item, ["status", "stance_change"], null),
  }));
}

function metrics(payload = {}, left, right) {
  const derivedPathway =
    Number.isFinite(Number(left.pathwayCount)) &&
    Number.isFinite(Number(right.pathwayCount))
      ? Number(right.pathwayCount) - Number(left.pathwayCount)
      : null;

  const derivedRoots =
    Number.isFinite(Number(left.rootCount)) &&
    Number.isFinite(Number(right.rootCount))
      ? Number(right.rootCount) - Number(left.rootCount)
      : null;

  const derivedConvergence =
    Number.isFinite(Number(left.convergence)) &&
    Number.isFinite(Number(right.convergence))
      ? Number(right.convergence) - Number(left.convergence)
      : null;

  const derivedDivergence =
    Number.isFinite(Number(left.divergence)) &&
    Number.isFinite(Number(right.divergence))
      ? Number(right.divergence) - Number(left.divergence)
      : null;

  return {
    topologyDistance: recursiveNumber(payload, [
      "topology_distance",
      "configuration_topology_distance",
    ]),
    rootTurnover: recursiveNumber(payload, [
      "root_turnover",
      "independent_root_turnover",
    ]),
    pathwayDelta: preferDerived(
      recursiveNumber(payload, [
        "pathway_delta",
        "pathway_count_delta",
        "pathways_delta",
      ]),
      derivedPathway
    ),
    affordingDelta: recursiveNumber(payload, [
      "affording_delta",
      "affording_pathway_delta",
      "affording_pathway_count_delta",
      "affords_delta",
    ]),
    constrainingDelta: recursiveNumber(payload, [
      "constraining_delta",
      "constraining_pathway_delta",
      "constraining_pathway_count_delta",
      "constrains_delta",
    ]),
    rootDelta: preferDerived(
      recursiveNumber(payload, [
        "root_delta",
        "independent_root_count_delta",
        "independent_roots_delta",
      ]),
      derivedRoots
    ),
    convergenceDelta: preferDerived(
      recursiveNumber(payload, [
        "convergence_delta",
        "convergence_score_delta",
      ]),
      derivedConvergence
    ),
    divergenceDelta: preferDerived(
      recursiveNumber(payload, [
        "divergence_delta",
        "divergence_score_delta",
      ]),
      derivedDivergence
    ),
  };
}

function touchesCurrent(delta, currentStateId) {
  if (!currentStateId || !delta || typeof delta !== "object") return false;
  return Object.entries(delta).some(
    ([key, value]) =>
      key.toLowerCase().includes("state") &&
      String(value) === String(currentStateId)
  );
}

export default function ConfigurationCompare({
  open,
  onClose,
  capabilityId,
  capabilityLabel,
  currentStateId,
  states,
  deltas,
  onOpenState,
}) {
  const suppliedDeltas = useMemo(
    () => (Array.isArray(deltas) ? deltas : []),
    [deltas]
  );

  const [catalog, setCatalog] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;

    let alive = true;
    setError(null);
    setDetail(null);

    if (suppliedDeltas.length) {
      const ordered = [...suppliedDeltas].sort((a, b) => {
        const aCurrent = touchesCurrent(a, currentStateId) ? 1 : 0;
        const bCurrent = touchesCurrent(b, currentStateId) ? 1 : 0;
        return bCurrent - aCurrent;
      });
      setCatalog(ordered);
      setSelectedId(deltaId(ordered[0]));
      return () => {
        alive = false;
      };
    }

    setLoading(true);

    researchApi
      .listDeltas({
        capabilityId,
        limit: 100,
        offset: 0,
      })
      .then((payload) => {
        if (!alive) return;
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
          ? payload.items
          : [];

        setCatalog(items);
        setSelectedId(deltaId(items[0]));
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || String(err));
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, capabilityId, currentStateId, suppliedDeltas]);

  useEffect(() => {
    if (!open || !selectedId) return;

    let alive = true;
    setLoading(true);
    setError(null);

    researchApi
      .getDelta(selectedId)
      .then((payload) => {
        if (!alive) return;
        setDetail(payload);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || String(err));
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, selectedId]);

  useEffect(() => {
    if (!open) return undefined;

    function onKey(event) {
      if (event.key === "Escape") onClose?.();
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const [left, right] = findStatePair(
    detail || {},
    states || [],
    currentStateId
  );

  const deltaMetrics = metrics(detail || {}, left, right);
  const stanceChanges = normalizeRootStanceChanges(detail || {});

  return (
    <div className="rr-delta-modal-layer">
      <button
        type="button"
        className="rr-delta-modal-backdrop"
        aria-label="Close configuration comparison"
        onClick={onClose}
      />

      <section
        className="rr-delta-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Configuration comparison"
      >
        <header className="rr-delta-modal-head">
          <div>
            <span className="v25a-eyebrow">DELTA / CONFIGURATION COMPARE</span>
            <h2>{capabilityLabel}</h2>
            <p>
              Same capability · different materialized configurations ·
              comparative topology
            </p>
          </div>

          <button type="button" onClick={onClose}>
            Lukk ×
          </button>
        </header>

        <ComparativeBoundary />

        {catalog.length > 1 ? (
          <nav className="rr-delta-switcher" aria-label="Choose comparative delta">
            <span>Available comparisons</span>
            <div>
              {catalog.map((item, index) => {
                const id = deltaId(item);
                return (
                  <button
                    type="button"
                    key={id || index}
                    className={id === selectedId ? "active" : ""}
                    onClick={() => setSelectedId(id)}
                  >
                    Δ {index + 1}
                    {touchesCurrent(item, currentStateId) ? " · current" : ""}
                  </button>
                );
              })}
            </div>
          </nav>
        ) : null}

        {loading && !detail ? (
          <div className="rr-delta-state">
            <i />
            <b>Loading comparative topology…</b>
          </div>
        ) : error ? (
          <div className="rr-delta-state error">
            <b>Could not load comparative delta</b>
            <span>{error}</span>
          </div>
        ) : detail ? (
          <>
            <DeltaTopology
              capabilityLabel={capabilityLabel}
              left={left}
              right={right}
              currentStateId={currentStateId}
              onOpenState={onOpenState}
            />

            <DeltaInspector
              metrics={deltaMetrics}
              rootStanceChanges={stanceChanges}
              left={left}
              right={right}
            />

            <ResultantComparison
              leftOrientation={left.orientation}
              rightOrientation={right.orientation}
            />

            <section className="rr-delta-core-insight">
              <span className="v25a-eyebrow">CORE INSIGHT</span>
              <p>
                Configuration difference is the analytical object. A stable
                resultant does not imply an unchanged field, and a changed
                topology does not imply a temporal event.
              </p>
            </section>
          </>
        ) : (
          <div className="rr-delta-state">
            <b>No comparative delta is available for this capability.</b>
          </div>
        )}
      </section>
    </div>
  );
}
